/**
 * Serialisation for the Scenarios list view state. Everything that decides *which* rows are shown
 * and in what order lives in the URL query string, so Back from a scenario (and a shared link)
 * restores the list exactly.
 */

export const PAGE_SIZE_OPTIONS = [10, 25, 50];

export const SORT_FIELDS = ['name', 'createdAt', 'updatedAt'];
export const SORT_DIRECTIONS = ['asc', 'desc'];

export const DEFAULT_QUERY = {
    search: '',
    responsibleUserId: '',
    ownerId: '',
    createdFrom: '',
    createdTo: '',
    sortBy: 'updatedAt',
    sortDirection: 'desc',
    page: 1,
    pageSize: 10,
};

const oneOf = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);
const isIsoDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v ?? '');

/** The direction a column sorts by when you first click it: A→Z for name, newest-first for dates. */
export const naturalDirection = (sortBy) => (sortBy === 'name' ? 'asc' : 'desc');

export const parseQuery = (searchParams) => {
    const pageRaw = parseInt(searchParams.get('page'), 10);
    const pageSizeRaw = parseInt(searchParams.get('pageSize'), 10);
    const sortBy = oneOf(searchParams.get('sortBy'), SORT_FIELDS, 'updatedAt');
    return {
        search: searchParams.get('search') ?? '',
        responsibleUserId: searchParams.get('responsibleUserId') ?? '',
        ownerId: searchParams.get('ownerId') ?? '',
        createdFrom: isIsoDate(searchParams.get('createdFrom')) ? searchParams.get('createdFrom') : '',
        createdTo: isIsoDate(searchParams.get('createdTo')) ? searchParams.get('createdTo') : '',
        sortBy,
        sortDirection: oneOf(searchParams.get('sortDirection'), SORT_DIRECTIONS, naturalDirection(sortBy)),
        page: Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1,
        pageSize: PAGE_SIZE_OPTIONS.includes(pageSizeRaw) ? pageSizeRaw : DEFAULT_QUERY.pageSize,
    };
};

/** Only non-default values end up in the URL, so a pristine list has a clean `/scenarios`. */
export const toSearchParams = (query) => {
    const params = new URLSearchParams();
    if (query.search) params.set('search', query.search);
    if (query.responsibleUserId) params.set('responsibleUserId', query.responsibleUserId);
    if (query.ownerId) params.set('ownerId', query.ownerId);
    if (query.createdFrom) params.set('createdFrom', query.createdFrom);
    if (query.createdTo) params.set('createdTo', query.createdTo);
    if (query.sortBy !== DEFAULT_QUERY.sortBy) params.set('sortBy', query.sortBy);
    // Persist direction whenever it isn't this column's natural default (so name+desc survives a refresh).
    if (query.sortDirection !== naturalDirection(query.sortBy)) params.set('sortDirection', query.sortDirection);
    if (query.page !== 1) params.set('page', String(query.page));
    if (query.pageSize !== DEFAULT_QUERY.pageSize) params.set('pageSize', String(query.pageSize));
    return params;
};

/** Changes to anything except the page number send the user back to page 1. */
export const CHANGES_THAT_RESET_PAGE = [
    'search', 'responsibleUserId', 'ownerId', 'createdFrom', 'createdTo', 'sortBy', 'sortDirection', 'pageSize',
];

/** API params for ScenarioService.getList — maps the empty-string "no filter" sentinels to null. */
export const toApiParams = (query) => ({
    page: query.page,
    pageSize: query.pageSize,
    name: query.search || '',
    sortBy: query.sortBy,
    sortDirection: query.sortDirection,
    responsibleUserId: query.responsibleUserId || null,
    ownerId: query.ownerId || null,
    createdFrom: query.createdFrom || null,
    createdTo: query.createdTo || null,
});

export const hasActiveFilters = (query) =>
    !!(query.search || query.responsibleUserId || query.ownerId || query.createdFrom || query.createdTo);
